import { Box, Divider, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { FC, Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader, Page } from '../../components';
import { getCustomNavigationDetail, getCustomPage, getCustomPageSections } from '../../fetch';
import { ICustomPageDetail, ICustomSubNavigationDetail, SectionType } from '../../models';
import { AccordionSection } from './AccordionSection';
import { FeedbackSection } from './FeedbackSection';
import { LinkBlockSection } from './LinkBlockSection';
import { MobileAppSection } from './MobileAppSection';
import { AvidWidget } from './AvidWidget';
import { YouTubeVideoSection } from './YouTubeVideoSection';
import clsx from 'clsx';
import { HeroSection } from './HeroSection';
import { IFrameSection } from './IFrameSection';

const NUM_SECTIONS_TO_LOAD = 3;

interface ICustomPage {
  pageSlug?: string;
}
export const CustomPage: FC<ICustomPage> = ({ pageSlug }) => {
  const classes = useStyles();

  const [customPage, setCustomPage] = useState<ICustomPageDetail | null>(null);
  const [customNavigation, setCustomNavigation] = useState<ICustomSubNavigationDetail | null>(null);
  const [isLoadingPage, setLoadingPage] = useState<boolean>(false);
  const [isLoadingSections, setLoadingSections] = useState<{
    [id: number]: boolean;
  }>({});

  const { slug }: { slug: string } = useParams();
  const fetchPage = async () => {
    try {
      setLoadingPage(true);
      let data = await getCustomPage(slug ?? pageSlug, NUM_SECTIONS_TO_LOAD);
      if (data.customNavigationId) {
        const navData = await getCustomNavigationDetail(data.customNavigationId);
        setCustomNavigation(navData);
      }

      const updateSections = data.sections.map((sec, index) => {
        const hasVideoSection = sec.sections?.filter(sub => sub.type === 'YouTubeVideoSection')
          .length
          ? true
          : false;
        const hasHeroSection = sec.sections?.filter(sub => sub.type === 'HeroSection').length
          ? true
          : false;
        let sectionClass = '';
        if (hasVideoSection) {
          sectionClass += ' videoSection';
        }
        if (hasHeroSection) {
          sectionClass += ' heroSection';
        }
        return {
          ...sec,
          sectionClass: sectionClass !== '' ? sectionClass : null,
          hasLoadedSubSections: index + 1 <= NUM_SECTIONS_TO_LOAD ? true : false
        };
      });
      data = { ...data, sections: updateSections };
      setCustomPage(data);
      fetchSections(data);
    } catch (e) {
      console.log(e);
    } finally {
      setLoadingPage(false);
    }
  };
  const fetchSections = async (customPageParam: ICustomPageDetail) => {
    // make a copy
    let copy = JSON.parse(JSON.stringify(customPageParam));
    // concurrent async call
    Promise.all(
      // slice the sections so we are only loading the ones we haven't pre-loaded
      customPageParam?.sections?.slice(NUM_SECTIONS_TO_LOAD).map(async (section, index) => {
        try {
          // set a loader
          setLoadingSections({
            ...isLoadingSections,
            [section.customPageSectionId]: true
          });
          // call the endpoint
          const res = await getCustomPageSections(
            customPageParam?.customPageId,
            section.customPageSectionId
          );
          // set the sections index based on where it starts plus the number of sections we pre loaded
          // and set it with the new data from API
          copy.sections[index + NUM_SECTIONS_TO_LOAD].sections = res;
          setCustomPage(copy);
        } catch (e) {
          console.log(e);
        } finally {
          setLoadingSections({
            ...isLoadingSections,
            [section.customPageSectionId]: false
          });
        }
      })
    );
  };
  // set the title of the page while it loads
  useEffect(() => {
    document.title = 'BuilderLinq';
  }, []);

  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, pageSlug]);

  if (isLoadingPage) {
    return <Loader position="centered" type="fullscreen" />;
  }
  return (
    <Page
      title={customPage?.name}
      hasNoContentPadding={true}
      showSideNav={customPage?.navigationType !== 'None'}
      showBreadcrumbs={false}
      disableDesktopCollapse={true}
      showHelp={false}
      sideBarLinks={
        customPage?.navigationType === 'Custom'
          ? customNavigation?.subNavigation?.map(subNav => {
              return {
                name: subNav.name,
                url: subNav.url,
                links: subNav?.subNavigation?.map(subNav2 => {
                  return {
                    name: subNav2.name,
                    url: subNav2.url
                  };
                })
              };
            })
          : []
      }
    >
      {customPage?.sections?.map((sec, index) => {
        return (
          <section key={`${index}`} className={clsx(classes.section, sec.sectionClass)}>
            {sec.text && (
              <Box className={classes.headerWrapper}>
                <Typography className={classes.sectionHeader} component="h2">
                  {sec.text}
                </Typography>
                <Divider className={classes.sectionDivider} />
              </Box>
            )}
            {sec.name === 'Feedback' && <AvidWidget />}
            {sec.subText && (
              <Typography
                className={
                  sec.name === 'Business Statement'
                    ? classes.subTextBusinessStatement
                    : classes.subText
                }
              >
                {sec.subText}
              </Typography>
            )}
            <div className={classes.subSectionWrapper}>
              {isLoadingSections[sec.customPageSectionId] && !sec.hasLoadedSubSections && (
                <Loader position="centered" type="inline" />
              )}
              {sec?.sections?.length > 0 &&
                sec?.sections?.map((s, index) => {
                  return (
                    <Fragment key={`${index}`}>
                      {s.type === SectionType.ACCORDION_SECTION && <AccordionSection section={s} />}
                      {s.type === SectionType.LINK_BLOCK_SECTION && (
                        <LinkBlockSection section={s} sectionName={sec.name} />
                      )}
                      {s.type === SectionType.YOU_TUBE_VIDEO_SECTION && (
                        <YouTubeVideoSection section={s} />
                      )}
                      {s.type === SectionType.Mobile_App_Section && (
                        <MobileAppSection section={s} />
                      )}
                      {s.type === SectionType.HERO && <HeroSection section={s} />}
                      {s.type === SectionType.IFRAME && <IFrameSection section={s} />}
                    </Fragment>
                  );
                })}
            </div>
            {sec.name === 'Feedback' && <FeedbackSection />}
          </section>
        );
      })}
    </Page>
  );
};

const useStyles = makeStyles<Theme>(theme => ({
  section: {
    marginBottom: theme.spacing(4),
    maxWidth: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      maxWidth: '1280px'
    },
    '&:first-child': {
      marginTop: theme.spacing(4),
      '&.heroSection': {
        marginTop: 0
      }
    }
  },
  subText: {
    textAlign: 'center',
    padding: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3)
    }
  },
  subTextBusinessStatement: {
    textAlign: 'center',
    fontWeight: 900,
    padding: '1rem',
    maxWidth: '90%',
    margin: '0 auto',
    lineHeight: 1.2,
    fontSize: '1.25rem',
    [theme.breakpoints.up('md')]: {
      padding: '2rem',
      maxWidth: '80%'
    }
  },
  headerWrapper: {
    padding: theme.spacing(0.5),
    backgroundColor: theme.palette.grey[300],
    '.videoSection &': {
      backgroundColor: theme.palette.common.white
    }
  },
  sectionHeader: {
    fontSize: '2rem',
    [theme.breakpoints.up('sm')]: {
      fontSize: '3rem'
    },
    fontWeight: 900,
    color: '#006938',
    letterSpacing: 2,
    textAlign: 'center',

    '.videoSection &': {
      color: theme.palette.secondary.main,
      textTransform: 'uppercase'
    }
  },
  sectionDivider: {
    borderColor: theme.palette.secondary.main,
    width: '100%',
    maxWidth: '178px',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: theme.spacing(1),
    display: 'none',
    '.videoSection &': {
      display: 'block'
    }
  },
  subTextWrapper: {
    padding: theme.spacing(4, 0.5, 0.5, 0.5)
  },
  sectionSubText: {
    textAlign: 'center'
  },
  subSectionWrapper: {
    padding: theme.spacing(2, 3),
    '.heroSection &': {
      padding: 0
    }
  }
}));
